import { useEffect, useState } from "react";
import { priceRanges } from "@/mocks/products";

export interface FilterOption {
  id: string;
  label: string;
}

interface SidebarFiltersProps {
  categories: FilterOption[];
  selectedCategory: string;
  onCategoryChange: (id: string) => void;
  selectedPrices: string[];
  onPriceToggle: (id: string) => void;
  colorOptions: FilterOption[];
  selectedColors: string[];
  onColorToggle: (id: string) => void;
  materialOptions: FilterOption[];
  selectedMaterials: string[];
  onMaterialToggle: (id: string) => void;
}

function SectionTitle({ children }: { children: string }) {
  return (
    <h3 className="mb-4 text-xs font-semibold uppercase tracking-widest text-foreground-900">
      {children}
    </h3>
  );
}

function CheckRow({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2.5 py-1.5 text-left"
    >
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors ${
          checked ? "border-primary-500 bg-primary-500 text-background-50" : "border-background-400 bg-background-50"
        }`}
      >
        {checked && <i className="ri-check-line text-[11px]"></i>}
      </span>
      <span className="text-sm text-foreground-700">{label}</span>
    </button>
  );
}

export default function SidebarFilters({
  categories,
  selectedCategory,
  onCategoryChange,
  selectedPrices,
  onPriceToggle,
  colorOptions,
  selectedColors,
  onColorToggle,
  materialOptions,
  selectedMaterials,
  onMaterialToggle,
}: SidebarFiltersProps) {
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const sync = () => setExpanded(media.matches);
    sync(); media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);
  return (
    <aside className="catalog-filters w-full shrink-0 lg:w-[250px]"><details open={expanded} onToggle={event => setExpanded(event.currentTarget.open)}><summary>Categorias e filtros <span aria-hidden="true">⌄</span></summary><div className="filter-sections">
      <div className="mb-8">
        <SectionTitle>Categorias</SectionTitle>
        <ul className="space-y-0.5">
          {categories.map((category) => {
            const active = selectedCategory === category.id;
            return (
              <li key={category.id}>
                <button
                  type="button"
                  onClick={() => onCategoryChange(category.id)}
                  className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                    active
                      ? "bg-background-100 font-medium text-primary-800 border-l-2 border-primary-500"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {category.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mb-8 border-t border-background-200 pt-6">
        <SectionTitle>Faixa de Preço</SectionTitle>
        {priceRanges.map((range) => (
          <CheckRow
            key={range.id}
            label={range.label}
            checked={selectedPrices.includes(range.id)}
            onToggle={() => onPriceToggle(range.id)}
          />
        ))}
      </div>

      {colorOptions.length > 0 && (
        <div className="mb-8 border-t border-background-200 pt-6">
          <SectionTitle>Cor</SectionTitle>
          <div className="space-y-1">
            {colorOptions.map((color) => (
              <CheckRow
                key={color.id}
                label={color.label}
                checked={selectedColors.includes(color.id)}
                onToggle={() => onColorToggle(color.id)}
              />
            ))}
          </div>
        </div>
      )}

      {materialOptions.length > 0 && (
        <div className="border-t border-background-200 pt-6">
          <SectionTitle>Material</SectionTitle>
          {materialOptions.map((material) => (
            <CheckRow
              key={material.id}
              label={material.label}
              checked={selectedMaterials.includes(material.id)}
              onToggle={() => onMaterialToggle(material.id)}
            />
          ))}
        </div>
      )}
    </div></details></aside>
  );
}
